import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import {
  getEffectiveMaxWeightsFn,
  getLogsForDayFn,
  getRecentLogsFn,
  getRecentLogsSchema,
  getSummarySchema,
  getUserLogsFn,
  getUserLogsSchema,
  getUserMaxWeightsFn,
  getWorkoutSummaryFn,
} from "./server";

export const getUserMaxWeightsOptions = () =>
  queryOptions({
    queryKey: ["userMaxWeights"],
    queryFn: () => getUserMaxWeightsFn(),
    staleTime: 1000 * 60 * 5,
  });

export const getEffectiveMaxWeightsOptions = (exerciseIds: string[]) =>
  queryOptions({
    queryKey: ["effectiveMaxWeights", exerciseIds],
    queryFn: () => getEffectiveMaxWeightsFn({ data: { exerciseIds } }),
    staleTime: 1000 * 60 * 5,
    enabled: exerciseIds.length > 0,
  });

export const getUserLogsOptions = (
  options?: z.infer<typeof getUserLogsSchema>,
) =>
  queryOptions({
    queryKey: ["userWorkoutLogs", options],
    queryFn: () => getUserLogsFn({ data: options ?? {} }),
  });

export const getLogsForDayOptions = (
  programId: string,
  dayId: string,
  date?: Date,
) =>
  queryOptions({
    queryKey: ["dayWorkoutLogs", programId, dayId, date?.toISOString()],
    queryFn: () => getLogsForDayFn({ data: { programId, dayId, date } }),
    staleTime: 1000 * 30, // 30s — collection manages live data, avoid unnecessary refetches
    // Video işlenirken (pending/processing) 4 saniyede bir polling yap,
    // tüm videolar done/error olunca polling durur
    refetchInterval: (query) => {
      const logs = query.state.data?.body?.logs ?? [];
      const hasProcessing = logs.some(
        (l) =>
          l.videoProcessingStatus === "pending" ||
          l.videoProcessingStatus === "processing",
      );
      return hasProcessing ? 4000 : false;
    },
  });

export const getRecentLogsOptions = (
  options: z.infer<typeof getRecentLogsSchema>,
) =>
  queryOptions({
    queryKey: ["recentExerciseLogs", options.exerciseId, options.limit],
    queryFn: () => getRecentLogsFn({ data: options }),
  });

export const getWorkoutSummaryOptions = (
  options?: z.infer<typeof getSummarySchema>,
) =>
  queryOptions({
    queryKey: ["workoutSummary", options],
    queryFn: () => getWorkoutSummaryFn({ data: options ?? {} }),
  });
